import { useMemo } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { useAnalyticsQuery } from '../../api/queries';
import { formatNumber, isoWeekStartLabel } from '../../lib/format';
import { TRAFFIC_PALETTE } from '../../lib/charts';
import ExpandableTable from '../../components/ExpandableTable';
import MetricCard from '../../components/MetricCard';
import InfoHint from '../../components/InfoHint';
import SubTabLoading from './SubTabLoading';

type Props = { af: string };

export default function WebsiteSubTab({ af }: Props) {
  const { data, isLoading, error } = useAnalyticsQuery(af);

  const newUsersChartData = useMemo(() => {
    if (!data?.usersOverTime?.length) return null;
    return {
      labels: data.usersOverTime.map((w) => isoWeekStartLabel(w.week)),
      datasets: [
        {
          label: 'New Users',
          data: data.usersOverTime.map((w) => w.newUsers),
          borderColor: '#2a5a8c',
          backgroundColor: 'rgba(42, 90, 140, 0.08)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#2a5a8c',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [data]);

  const trafficChartData = useMemo(() => {
    if (!data?.trafficSources?.length) return null;
    return {
      labels: data.trafficSources.map((t) => t.channel),
      datasets: [
        {
          data: data.trafficSources.map((t) => t.sessions),
          backgroundColor: TRAFFIC_PALETTE.slice(0, data.trafficSources.length),
          borderColor: 'white',
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  if (isLoading) {
    return (
      <SubTabLoading
        title="Loading Website Performance Data..."
        sub="Fetching metrics from Google Analytics"
      />
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Website Data Unavailable</h3>
        <p>Could not load GA4 data: {(error as Error).message}</p>
      </div>
    );
  }
  // `empty` is a truthy object, not null, so a bare `!data` check sails past it
  // and every downstream read of kpis throws. Franchisees with no separate site
  // (site_url is a note, not a URL) never get GA4 data at all, so this is a
  // permanent state for them, not just a cold-cache blip.
  if (!data || data.empty || !data.kpis) {
    return (
      <div className="tab-placeholder">
        <h3>No website analytics yet</h3>
        <p>
          {data?.refreshing
            ? `We're fetching Google Analytics data for ${af} — check back in a few minutes.`
            : `No Google Analytics data is available for ${af}. This is expected when the territory has no separate website of its own.`}
        </p>
      </div>
    );
  }

  const { kpis } = data;

  return (
    <>
      <div className="metrics-row">
        <MetricCard
          label="Sessions"
          value={formatNumber(kpis.sessions)}
          change={kpis.sessionsChange}
          hint="A visit to your website. One person can have several sessions."
        />
        <MetricCard label="Chats" value={formatNumber(kpis.chats)} change={kpis.chatsChange} />
        <MetricCard
          label="Phone Clicks"
          value={formatNumber(kpis.phoneClicks)}
          change={kpis.phoneClicksChange}
        />
        <MetricCard
          label="Webform Submissions"
          value={formatNumber(kpis.webformSubmissions)}
          change={kpis.webformSubmissionsChange}
        />
      </div>

      <div className="charts-row">
        <div className="chart-panel">
          <h3>
            New Users (Last 90 Days)
            <InfoHint text="People visiting your website for the first time in this period." label="New Users" />
          </h3>
          <div className="chart-container">
            {newUsersChartData ? (
              <Line
                data={newUsersChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'New users',
                        color: '#5a6675',
                        font: { size: 12, weight: 500 },
                      },
                      ticks: { color: '#8a95a5', font: { size: 12 } },
                      grid: { color: '#f0f2f5' },
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Week of the year',
                        color: '#5a6675',
                        font: { size: 12, weight: 500 },
                      },
                      ticks: { color: '#8a95a5', font: { size: 12 } },
                      grid: { display: false },
                    },
                  },
                }}
              />
            ) : (
              <p style={{ color: '#8a95a5', fontSize: 13 }}>No data</p>
            )}
          </div>
        </div>
        <div className="chart-panel">
          <h3>How People Find Your Website</h3>
          <div className="chart-container">
            {trafficChartData ? (
              <Doughnut
                data={trafficChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#3a4a5a',
                        font: { size: 11 },
                        padding: 12,
                        usePointStyle: true,
                        pointStyle: 'circle',
                      },
                    },
                  },
                }}
              />
            ) : (
              <p style={{ color: '#8a95a5', fontSize: 13 }}>No data</p>
            )}
          </div>
        </div>
      </div>

      {data.geoData && data.geoData.length > 0 ? (
        <ExpandableTable
          title="Where is Your Site Traffic Coming From?"
          rows={data.geoData}
          columns={[
            { header: 'City', render: (g) => g.city },
            { header: 'Region', render: (g) => g.region },
            { header: 'Sessions', render: (g) => g.sessions },
          ]}
          expandLabel={(n) => `Show all ${n} cities`}
        />
      ) : (
        <div className="geo-placeholder">
          <h3>Where is Your Site Traffic Coming From?</h3>
          <p>No geographic data available for this period</p>
        </div>
      )}

      {data.popularPages && data.popularPages.length > 0 && (
        <ExpandableTable
          title="Most Popular Pages"
          rows={data.popularPages}
          columns={[
            { header: 'Page Title', render: (p) => p.title },
            { header: 'New Users', render: (p) => p.newUsers },
            { header: 'Engaged Sessions', render: (p) => p.engagedSessions },
          ]}
          expandLabel={(n) => `Show all ${n} pages`}
        />
      )}

      <div className="metrics-footer">
        Data from GA4 • {data.date_range} • Page path: {data.page_path}
      </div>
    </>
  );
}
